import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';

const STATUSES = ['new', 'interested', 'follow-up', 'enrolled', 'not-interested'];
const STATUS_COLORS = { new: '#6366f1', interested: '#f97316', 'follow-up': '#facc15', enrolled: '#22c55e', 'not-interested': '#475569' };

export default function Leads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editing, setEditing] = useState(null); // phone being edited

  useEffect(() => { loadLeads(); }, [filter, page]);

  async function loadLeads() {
    const params = new URLSearchParams({ page, limit: 20 });
    if (filter) params.append('status', filter);
    const { data } = await api.get(`/api/leads?${params}`);
    setLeads(data.data);
    setTotalPages(data.pages);
  }

  async function updateStatus(phone, status) {
    await api.patch(`/api/leads/${encodeURIComponent(phone)}`, { status });
    setLeads((prev) => prev.map((l) => l.phone === phone ? { ...l, status } : l));
  }

  async function saveNotes(phone, notes) {
    await api.patch(`/api/leads/${encodeURIComponent(phone)}`, { notes });
    setLeads((prev) => prev.map((l) => l.phone === phone ? { ...l, notes } : l));
    setEditing(null);
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Leads</h1>
        <div style={styles.filters}>
          <button style={{ ...styles.filterBtn, ...(filter === '' ? styles.filterActive : {}) }} onClick={() => { setFilter(''); setPage(1); }}>All</button>
          {STATUSES.map((s) => (
            <button
              key={s}
              style={{ ...styles.filterBtn, ...(filter === s ? { ...styles.filterActive, background: STATUS_COLORS[s] + '33', color: STATUS_COLORS[s] } : {}) }}
              onClick={() => { setFilter(s); setPage(1); }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div style={styles.table}>
        <div style={styles.tableHeader}>
          <span style={{ flex: 1.2 }}>Name / Phone</span>
          <span style={{ flex: 1 }}>Interest</span>
          <span style={{ flex: 1 }}>Background</span>
          <span style={{ flex: 0.8 }}>Status</span>
          <span style={{ flex: 1 }}>Notes</span>
          <span style={{ flex: 0.6 }}>Actions</span>
        </div>

        {leads.length === 0 && <p style={styles.empty}>No leads found</p>}

        {leads.map((lead) => (
          <div key={lead._id} style={styles.row}>
            <div style={{ flex: 1.2 }}>
              <div style={styles.leadName}>{lead.name || '—'}</div>
              <div style={styles.leadPhone}>{lead.phone.replace('@c.us', '')}</div>
              {lead.wantsHuman && <span style={styles.humanBadge}>🙋 Human</span>}
            </div>
            <div style={{ flex: 1, fontSize: 13, color: '#94a3b8' }}>{lead.interest || '—'}</div>
            <div style={{ flex: 1, fontSize: 13, color: '#94a3b8' }}>{lead.background || '—'}</div>
            <div style={{ flex: 0.8 }}>
              <select
                style={{ ...styles.statusSelect, borderColor: STATUS_COLORS[lead.status] }}
                value={lead.status}
                onChange={(e) => updateStatus(lead.phone, e.target.value)}
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              {editing === lead.phone ? (
                <NoteEditor
                  initial={lead.notes}
                  onSave={(n) => saveNotes(lead.phone, n)}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <span style={styles.notesText} onClick={() => setEditing(lead.phone)}>
                  {lead.notes || <span style={{ color: '#475569', fontStyle: 'italic' }}>Add note...</span>}
                </span>
              )}
            </div>
            <div style={{ flex: 0.6, display: 'flex', gap: 6 }}>
              <button style={styles.actionBtn} onClick={() => navigate(`/chats?phone=${encodeURIComponent(lead.phone)}`)}>💬</button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button style={styles.pageBtn} disabled={page === 1} onClick={() => setPage(page - 1)}>←</button>
          <span style={{ color: '#94a3b8', fontSize: 13 }}>{page} / {totalPages}</span>
          <button style={styles.pageBtn} disabled={page === totalPages} onClick={() => setPage(page + 1)}>→</button>
        </div>
      )}
    </div>
  );
}

function NoteEditor({ initial, onSave, onCancel }) {
  const [val, setVal] = useState(initial || '');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <textarea
        style={{ padding: 6, background: 'rgba(2, 5, 16, 0.8)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 6, color: '#e2e8f0', fontSize: 12, resize: 'none', fontFamily: 'inherit', outline: 'none' }}
        rows={2}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        autoFocus
      />
      <div style={{ display: 'flex', gap: 4 }}>
        <button style={{ ...styles.actionBtn, background: 'rgba(52,211,153,0.1)', color: '#34d399', borderColor: 'rgba(52,211,153,0.25)' }} onClick={() => onSave(val)}>Save</button>
        <button style={{ ...styles.actionBtn }} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '28px 32px', flex: 1, overflowY: 'auto', background: 'transparent' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 22, fontWeight: 700, color: '#e2e8f0', letterSpacing: -0.3 },
  filters: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  filterBtn: { padding: '6px 12px', borderRadius: 20, border: '1px solid rgba(99,102,241,0.18)', background: 'transparent', color: '#64748b', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' },
  filterActive: { background: 'rgba(99,102,241,0.15)', color: '#818cf8', borderColor: 'rgba(99,102,241,0.4)' },
  table: { background: 'rgba(6, 12, 28, 0.9)', borderRadius: 12, border: '1px solid rgba(99,102,241,0.12)', overflow: 'hidden', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' },
  tableHeader: { display: 'flex', padding: '12px 20px', borderBottom: '1px solid rgba(99,102,241,0.1)', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  row: { display: 'flex', padding: '14px 20px', borderBottom: '1px solid rgba(99,102,241,0.06)', alignItems: 'flex-start', gap: 8, fontSize: 13 },
  empty: { padding: 20, color: '#64748b', fontSize: 14 },
  leadName: { fontWeight: 600, color: '#e2e8f0', marginBottom: 2 },
  leadPhone: { color: '#64748b', fontSize: 12 },
  humanBadge: { fontSize: 10, padding: '2px 6px', background: 'rgba(244,114,182,0.12)', color: '#f472b6', borderRadius: 10, display: 'inline-block', marginTop: 4, border: '1px solid rgba(244,114,182,0.2)' },
  statusSelect: { background: 'rgba(2, 5, 16, 0.8)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 6, color: '#e2e8f0', padding: '4px 6px', fontSize: 12, cursor: 'pointer', width: '100%', fontFamily: 'inherit' },
  notesText: { fontSize: 12, color: '#64748b', cursor: 'pointer', lineHeight: 1.5 },
  actionBtn: { padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(99,102,241,0.18)', background: 'rgba(2, 5, 16, 0.7)', color: '#64748b', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' },
  pagination: { display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', marginTop: 16 },
  pageBtn: { padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(99,102,241,0.18)', background: 'rgba(6, 12, 28, 0.8)', color: '#e2e8f0', cursor: 'pointer', fontFamily: 'inherit' },
};
